import type { Instance } from "@gadgetinc/mobx-quick-tree";
import { RichString } from "@gadgetinc/widgets";
import { upperFirst } from "lodash";
import { ProblemSeverity } from "state-trees/src/problems/ProblemSeverity";
import type { ProblemMessage, ProblemsList } from "state-trees/src/problems/ProblemsList";
import type { BindingProps } from "./useBinding";

export type NodeWithProblems = object & {
  problems?: ProblemsList;
};

export const useProblems = <Node extends NodeWithProblems>(
  node: Node,
  property?: string
): { errors?: string; severity?: ProblemSeverity; rawErrors?: string } => {
  if (!property) {
    return { errors: undefined, severity: undefined, rawErrors: undefined };
  }

  let rawErrors;
  let errors;
  let severity: ProblemSeverity | undefined = undefined;
  if ("problems" in node) {
    if (node.problems?.anyProblemsOn(property)) {
      rawErrors = node.problems.problemsOn(property).map((msg: Instance<typeof ProblemMessage>) => {
        const message = msg.inContextMessage ? msg.inContextMessage : msg.message;
        return upperFirst(message);
      });
      errors = rawErrors.map((msg: string) => RichString.parseToString(msg, "", "")).join(". ");
      rawErrors = rawErrors.join(". ");
      severity = node.problems?.highestSeverity(property) ?? ProblemSeverity.Error;
    }
  }

  return { errors, severity, rawErrors };
};

export const useBoundNodeHasProblems = <Node extends NodeWithProblems>(props: BindingProps<Node, any> & { error?: boolean }) => {
  const { errors, severity } = useProblems(props.node, (props as any).property || (props as any).showErrorsOnProperty);

  const error = props.error || !!errors;

  return { error, severity };
};
